import { doc, getDoc, updateDoc, increment } from 'firebase/firestore';

// CALENDÁRIO DA LIGA — leitura e escrita dos jogos de uma jornada.
//
// O PROBLEMA QUE ISTO RESOLVE
// As jornadas estavam guardadas como ARRAYS de jogos, e o resultado de cada
// jogo era escrito com um caminho de campo:
//
//   updateDoc(ligaRef, { 'jornada_1.jogo_j1_0_0.status': 'encerrado' })
//
// O Firestore NÃO percorre arrays num caminho de campo — só percorre mapas.
// Esse update era recusado, por isso nem os resultados nem os pontos dos
// presentes chegavam alguma vez ao calendário: a classificação era escrita na
// subcoleção `inscritos`, mas a tabela de jogos ficava eternamente "pendente".
//
// A CORREÇÃO
// As jornadas passam a ser MAPAS indexados pelo idJogo:
//
//   jornada_1: { jogo_j1_0_0: {...}, jogo_j1_0_1: {...} }
//
// Assim o caminho de campo funciona — e, mais importante, o `increment()`
// continua a ser atómico. Com um array seria preciso ler-alterar-escrever o
// array inteiro, e dois adeptos a mandar presentes ao mesmo tempo perdiam um
// dos presentes. Num mapa, cada `increment` é aplicado pelo servidor.
//
// As ligas antigas (com jornadas em array) continuam a ser lidas e escritas —
// ver o ramo de compatibilidade em `atualizarJogoDaJornada`.

// Devolve os jogos de uma jornada como lista ordenada, venha ela em mapa
// (formato novo) ou em array (ligas criadas antes desta correção).
export function jogosDaJornada(dadosLiga, chaveJornada) {
  const bruto = dadosLiga?.[chaveJornada];
  if (!bruto) return [];
  const lista = Array.isArray(bruto) ? bruto : Object.values(bruto);
  // `ordem` fixa a apresentação; sem ela, a ordem das chaves de um mapa do
  // Firestore não é garantida e os jogos podiam saltitar no ecrã.
  return [...lista]
    .filter(Boolean)
    .sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0));
}

export function jornadaEstaEmArray(dadosLiga, chaveJornada) {
  return Array.isArray(dadosLiga?.[chaveJornada]);
}

// Marca um valor para ser somado em vez de substituído.
export const somar = (valor) => ({ __somar: valor });

// Atualiza campos de UM jogo dentro de uma jornada.
//
//   await atualizarJogoDaJornada(db, idLiga, 'jornada_1', 'jogo_j1_0_0', {
//     status: 'encerrado',
//     pontosP1: somar(250),
//   });
export async function atualizarJogoDaJornada(db, idLiga, chaveJornada, idJogo, campos) {
  if (!idLiga || !chaveJornada || !idJogo) return false;

  const refLiga = doc(db, 'ligas_ativas', idLiga);
  const snap = await getDoc(refLiga);
  if (!snap.exists()) return false;

  const bruto = snap.data()?.[chaveJornada];

  // ---- formato novo: mapa. Caminhos de campo e increment atómico. --------
  if (bruto && !Array.isArray(bruto)) {
    const alteracoes = {};
    for (const [campo, valor] of Object.entries(campos)) {
      alteracoes[`${chaveJornada}.${idJogo}.${campo}`] = (valor && valor.__somar !== undefined)
        ? increment(valor.__somar)
        : valor;
    }
    await updateDoc(refLiga, alteracoes);
    return true;
  }

  // ---- ligas antigas: array. Ler, alterar, escrever o array inteiro. -----
  // Perde a atomicidade do increment, mas é a única forma de lá chegar. Só
  // acontece em ligas criadas antes desta correção.
  if (Array.isArray(bruto)) {
    const lista = [...bruto];
    const indice = lista.findIndex((j) => j?.idJogo === idJogo);
    if (indice < 0) return false;

    const jogo = { ...lista[indice] };
    for (const [campo, valor] of Object.entries(campos)) {
      jogo[campo] = (valor && valor.__somar !== undefined)
        ? (Number(jogo[campo]) || 0) + valor.__somar
        : valor;
    }
    lista[indice] = jogo;
    await updateDoc(refLiga, { [chaveJornada]: lista });
    return true;
  }

  return false;
}

export function construirCalendarioLiga(jogadores) {
  const lista = [...jogadores];
  const numJogadores = lista.length;
  const totalRondas = numJogadores - 1;      // Se forem 10 jogadores, gera 9 jornadas
  const jogosPorRonda = numJogadores / 2;    // Se forem 10 jogadores, gera 5 jogos por jornada
  const jornadas = {};

  for (let r = 0; r < totalRondas; r++) {
    const ida = {};

    for (let j = 0; j < jogosPorRonda; j++) {
      const casa = (r + j) % (numJogadores - 1);
      let fora = (numJogadores - 1 - j + r) % (numJogadores - 1);
      if (j === 0) fora = numJogadores - 1;   // O jogador fixo do círculo de Berger

      const idIda = `jogo_j1_${r}_${j}`;
      ida[idIda] = {
        idJogo: idIda,
        ordem: j,
        p1: lista[casa],
        p2: lista[fora],
        status: 'pendente',
        pontosP1: 0,
        pontosP2: 0,
        prontoP1: false,
        prontoP2: false,
        // ⏳ CARIMBO DE PRAZO: Permite à vassoura de W.O. controlar as 24 horas do jogo
        geradoEm: Date.now(), 
        limiteWoEm: Date.now() + (24 * 60 * 60 * 1000) // Tranca síncrona de 24 horas para o jogo expirar
      };
    }

    // 🏆 PWA RECALIBRADA: Salva apenas a 1ª Volta, tornando a subida rápida e ultra-dinâmica!
    jornadas[`jornada_${r + 1}`] = ida;
  }

  return { jornadas, totalRondas, totalJornadas: totalRondas, jogosPorRonda };
}
